import React, { useEffect, useRef, useState } from 'react';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import type { CoworkMessage } from '../../types/cowork';
import { store } from '../../store';
import { configService } from '../../services/config';
import { localStore } from '../../services/store';
import { authService } from '../../services/auth';

interface CoworkQuickActionsProps {
  sessionId: string;
  messages: CoworkMessage[];
  isStreaming: boolean;
  onAction: (prompt: string) => void;
}

interface QuickAction {
  label: string;
  prompt: string;
}

/** 模块级缓存：sessionId → { msgKey, actions } */
const suggestionsCache = new Map<string, { msgKey: string; actions: QuickAction[] }>();

// ─── LLM 调用相关 ───

/** 提取最后一轮对话的关键上下文（轻量化） */
const buildContextSummary = (messages: CoworkMessage[]): string => {
  const parts: string[] = [];

  // 找到最后一条用户消息的位置
  let lastUserIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].type === 'user') { lastUserIdx = i; break; }
  }
  if (lastUserIdx === -1) return '';

  // 只取最后一轮：从最后一条用户消息到结尾
  const lastTurn = messages.slice(lastUserIdx);

  for (const msg of lastTurn) {
    if (msg.type === 'user') {
      parts.push(`用户: ${msg.content.slice(0, 200)}`);
    } else if (msg.type === 'assistant' && !msg.metadata?.isThinking) {
      parts.push(`助手: ${msg.content.slice(0, 500)}`);
    } else if (msg.type === 'tool_use' && msg.metadata?.toolName) {
      const tn = msg.metadata.toolName;
      const input = msg.metadata.toolInput as Record<string, unknown> | undefined;
      let s = `工具: ${tn}`;
      if (input) {
        if (tn === 'Bash' && typeof input.command === 'string') s += ` - ${input.command.slice(0, 80)}`;
        else if (typeof input.file_path === 'string') s += ` - ${input.file_path}`;
      }
      parts.push(s);
    }
    // 跳过 tool_result，减少 token 消耗
  }

  // 如果有更早的对话，取最后 2 轮用户消息作为主题摘要
  if (lastUserIdx > 0) {
    const earlier: string[] = [];
    let count = 0;
    for (let i = lastUserIdx - 1; i >= 0 && count < 2; i--) {
      if (messages[i].type === 'user') {
        earlier.unshift(`用户(历史): ${messages[i].content.slice(0, 100)}`);
        count++;
      }
    }
    if (earlier.length > 0) {
      parts.unshift(...earlier);
    }
  }

  return parts.join('\n');
};

const SUGGESTION_PROMPT = `根据对话上下文，推荐 3 个用户最可能的后续操作。

规则：
- 建议必须与当前对话主题紧密相关，针对用户刚才讨论的具体内容
- 禁止返回通用建议（如"获取帮助"、"你的能力"、"开始提问"、"了解更多"等）
- 标签 2-6 字，像按钮文字
- prompt 是完整的后续指令
- 只返回 JSON，无其他内容

格式：[{"label":"标签","prompt":"指令"},...]

对话：
`;

const resolveApiEndpoint = async (): Promise<{
  url: string;
  headers: Record<string, string>;
  modelId: string;
} | null> => {
  try {
    const selectedModel = store.getState().model.selectedModel;
    if (!selectedModel?.id) return null;
    const modelId = selectedModel.id;

    const gatewayCfg = await localStore.getItem<{ gateway_url: string; endpoint_path: string }>('gateway_config');
    const apiKey = authService.getBoBotApiKey();
    if (!gatewayCfg?.gateway_url || !apiKey) return null;
    const baseUrl = gatewayCfg.gateway_url.replace(/\/+$/, '') + gatewayCfg.endpoint_path;

    const url = baseUrl;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    };
    return { url, headers, modelId };
  } catch {
    return null;
  }
};

/** 带超时的 fetch，默认 8 秒 */
const fetchWithTimeout = async (
  options: Parameters<typeof window.electron.api.fetch>[0],
  timeoutMs = 8000
): Promise<Awaited<ReturnType<typeof window.electron.api.fetch>> | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });
  try {
    const result = await Promise.race([window.electron.api.fetch(options), timeout]);
    return result;
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const fetchSuggestions = async (messages: CoworkMessage[]): Promise<QuickAction[]> => {
  const context = buildContextSummary(messages);
  if (!context.trim()) return [];
  const endpoint = await resolveApiEndpoint();
  if (!endpoint) return [];

  try {
    const response = await fetchWithTimeout({
      url: endpoint.url,
      method: 'POST',
      headers: endpoint.headers,
      body: JSON.stringify({
        model: endpoint.modelId,
        messages: [{ role: 'user', content: SUGGESTION_PROMPT + context }],
        max_tokens: 1024,
        temperature: 0.7,
        stream: false,
      }),
    });

    if (!response?.ok) return [];
    const data = response.data as Record<string, unknown> | undefined;
    const content = String(
      (data?.choices as { message?: { content?: string } }[] | undefined)?.[0]?.message?.content ?? ''
    ).trim();
    if (!content) return [];

    const jsonMatch = content.match(/\[[\s\S]*\]/);
    if (!jsonMatch) return [];

    const parsed = JSON.parse(jsonMatch[0]);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item: unknown) =>
        item && typeof item === 'object' &&
        typeof (item as Record<string, unknown>).label === 'string' &&
        typeof (item as Record<string, unknown>).prompt === 'string'
      )
      .slice(0, 3)
      .map((item: Record<string, string>) => ({ label: item.label, prompt: item.prompt }));
  } catch {
    return [];
  }
};

// ─── 组件 ───

const CoworkQuickActions: React.FC<CoworkQuickActionsProps> = ({
  sessionId,
  messages,
  isStreaming,
  onAction,
}) => {
  // 初始化时从缓存恢复
  const cached = suggestionsCache.get(sessionId);
  const [actions, setActions] = useState<QuickAction[]>(cached?.actions ?? []);
  const [isLoading, setIsLoading] = useState(false);

  const fetchSeqRef = useRef(0);
  const prefetchedRef = useRef<QuickAction[] | null>(null);
  const prefetchInitiatedRef = useRef(false);
  const lastFetchKeyRef = useRef(cached?.msgKey ?? '');
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // 将结果写入缓存的辅助函数
  const cacheActions = (msgKey: string, result: QuickAction[]) => {
    suggestionsCache.set(sessionId, { msgKey, actions: result });
  };

  // ─── Streaming 期间预取（只在首次检测到足够消息时启动一次，不随消息更新重置）
  useEffect(() => {
    if (!isStreaming) {
      prefetchInitiatedRef.current = false;
      return;
    }

    // 功能未开启或消息不足时跳过
    if (!configService.getConfig().quickActionSuggestionsEnabled) return;
    if (messages.length < 3 || prefetchInitiatedRef.current) return;
    prefetchInitiatedRef.current = true;

    // 延迟 1.5 秒后启动预取（等待 streaming 产生足够上下文）
    const timer = setTimeout(async () => {
      try {
        const result = await fetchSuggestions(messagesRef.current);
        if (result.length > 0) {
          prefetchedRef.current = result;
        }
      } catch { /* ignore */ }
    }, 1500);

    return () => clearTimeout(timer);
  }, [isStreaming, messages.length >= 3]);

  // ─── Streaming 结束时展示建议
  useEffect(() => {
    if (isStreaming) {
      setActions([]);
      setIsLoading(false);
      lastFetchKeyRef.current = '';
      return;
    }

    // 功能未开启时不请求
    if (!configService.getConfig().quickActionSuggestionsEnabled) return;

    if (messages.length === 0) {
      setActions([]);
      return;
    }

    const lastMsg = messages[messages.length - 1];
    const fetchKey = `${messages.length}-${lastMsg.id}`;
    if (fetchKey === lastFetchKeyRef.current) return;
    lastFetchKeyRef.current = fetchKey;

    // 检查缓存是否命中（同一会话、同一消息位置）
    const cachedEntry = suggestionsCache.get(sessionId);
    if (cachedEntry && cachedEntry.msgKey === fetchKey && cachedEntry.actions.length > 0) {
      setActions(cachedEntry.actions);
      return;
    }

    // 检查是否有预取结果可用
    if (prefetchedRef.current && prefetchedRef.current.length > 0) {
      const result = prefetchedRef.current;
      prefetchedRef.current = null;
      setActions(result);
      cacheActions(fetchKey, result);
      return;
    }

    // 没有预取结果，发起新请求
    setIsLoading(true);
    const seq = ++fetchSeqRef.current;

    fetchSuggestions(messages)
      .then((result) => {
        if (fetchSeqRef.current !== seq) return;
        if (result.length > 0) {
          setActions(result);
          cacheActions(fetchKey, result);
        }
      })
      .finally(() => {
        if (fetchSeqRef.current === seq) setIsLoading(false);
      });
  }, [isStreaming, messages]);

  // 未开启对话辅助提示时不渲染
  const config = configService.getConfig();
  if (!config.quickActionSuggestionsEnabled) return null;

  // streaming 期间或无内容时不渲染
  if (isStreaming) return null;
  if (actions.length === 0 && !isLoading) return null;

  return (
    <div className="flex flex-wrap gap-2 px-4 py-2">
      <div className="max-w-3xl mx-auto w-full flex flex-wrap gap-2">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={() => onAction(action.prompt)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm
              dark:bg-claude-darkSurfaceHover bg-claude-surfaceHover
              dark:text-claude-darkText text-claude-text
              dark:hover:bg-claude-darkBorder hover:bg-claude-border
              transition-colors cursor-pointer"
          >
            <span>{action.label}</span>
            <ChevronRightIcon className="h-3.5 w-3.5 opacity-50" />
          </button>
        ))}
        {isLoading && (
          <span className="inline-flex items-center gap-1 px-3 py-2 text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
            <span className="inline-block w-1 h-1 rounded-full bg-current animate-pulse" />
            <span className="inline-block w-1 h-1 rounded-full bg-current animate-pulse" style={{ animationDelay: '0.2s' }} />
            <span className="inline-block w-1 h-1 rounded-full bg-current animate-pulse" style={{ animationDelay: '0.4s' }} />
          </span>
        )}
      </div>
    </div>
  );
};

export default CoworkQuickActions;
